import { AlertDto } from './alerts.model';
import { Moment } from 'moment';

import { ProfileBusinessPerson } from './business-person.model';
import { ContactInfo } from './contact-info.model';
import { CrewMember } from './crew-member.model';
import { EmergencyContact } from './emergency-contact.model';
import { Passenger } from './passenger.model';
import { PersonTags } from './person-tag.model';
import { Preferences } from './preferences.model';
import { getPrimary } from './primary-fns';
import { StaffInfo } from './staff-info.model';

export type Gender = 'M' | 'F';

interface PersonCore {
  active: boolean;
  prefix: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  suffix: string | null;
  tags: Set<PersonTags> | PersonTags[];
  preferredFirstName: string | null;
  preferredLastName: string | null;
  modifiedDate: string | null;
}

export interface Person extends PersonCore {
  readonly personId: number | null;
  dateOfBirth: Moment | string | null;
  cityOfBirth: string | null;
  stateProvinceOfBirth: string | null;
  citizenshipCountryId: number | null;

  weight: number | null;
  weightUnitId: number | null;
  gender: Gender | null;

  businessPerson: ProfileBusinessPerson;
  contactInfo: ContactInfo;
  staffInfo: StaffInfo | null;
  passenger: Passenger | null;
  emergencyContacts: EmergencyContact[];
  preferences: Preferences;
  alerts: AlertDto[];
  personStaffGroups: PersonStaffGroup[] | null;
  personChecklistRoleTypes: PersonChecklistRoleType[] | null;
  crewMember: CrewMember | null;
  notes: string | null;
}

export class PersonStaffGroup {
  personStaffGroupId: number;
  personId: number;
  staffGroupId: number;
  active: true;
  name: string;
}

export class PersonChecklistRoleType {
  personChecklistRoleTypeId: number;
  personId: number;
  checklistRoleTypeId: number;
  active: true;
  name: string;
}

export interface PersonSummary extends PersonCore {
  readonly id: number;
  businessPersonId: number | null;
  primaryEmailAddress: string | null;
  primaryPhoneNumber: string | null;
  primaryPhoneCode: string | null;
  hrTitle: string | null;
  employeeId: string | null;
  delimitedTags?: string;
  notes: string | null;
}

export function isPersonSaved(person: Person | null) {
  return person && person.personId && person.personId > 0;
}

export function isPersonSummary(
  personOrSummary: Person | PersonSummary
): personOrSummary is PersonSummary {
  return !!(personOrSummary && typeof (personOrSummary as Person).contactInfo === 'undefined');
}

export function getPrimaryEmailText(person: Person | PersonSummary) {
  if (isPersonSummary(person)) {
    return person.primaryEmailAddress;
  }
  const primary = getPrimary(person.contactInfo.emailAddresses);
  return primary ? primary.address : null;
}
export function getPrimaryPhoneText(person: Person | PersonSummary) {
  if (isPersonSummary(person)) {
    return person.primaryPhoneNumber;
  }
  const primary = getPrimary(person.contactInfo.phoneNumbers);
  return primary ? primary.number : null;
}
